/**
 * シングルモード（ステージ）データ — 純粋データ、DOM / three.js 非依存。
 *
 * 各ステージは createGame(config) にそのまま渡せる config を持つ。プレイヤーは
 * 常に先手（index 0・人間）、CPU は後手（index 1）。勝利条件は通常ルール
 * （自分のゴール行に到達）で、config.position が中盤の初期局面を与える。
 *
 * position の形 (src/core/index.js seedPosition 参照・全フィールド任意):
 *   pawns:          [{ col, row }, ...]  プレイヤーごと
 *   walls:          [{ col, row, orientation, owner }, ...]
 *   wallsRemaining: [n, ...]             プレイヤーごとの残り壁数
 *   currentPlayer:  0 | 1                手番
 *
 * cpu.level は既存の AI レベル id（easy / casual / normal / tough / hard）。
 * tier は難易度名（ステージ選択画面の見出し）。6ティア×各5ステージ = 30。
 *
 * 全ステージが seedPosition の検証（盤内・非交差・両者にゴール経路あり）を
 * 通ること（tests/stages.test.js）に加え、sim/stagecheck.mjs の代理AI
 * ミニシミュレーション（normal/hard を先手に立てて対戦）で難易度カーブを
 * 計測済み: normalProxy 勝率はティアが進むほど下がるよう調整してある
 * （games=8, seed=7。詳細な帯はコミットメッセージ参照）。
 */

export const STAGES = [
  // --- 入門 ---
  {
    id: 'stage-01',
    tier: '入門',
    title: 'はじめの一歩',
    description: 'ゴールはすぐそこ。壁を1枚よけて駆け抜けよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 4, row: 6 },
          { col: 4, row: 8 },
        ],
        walls: [
          { col: 3, row: 6, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [6, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'easy' },
  },
  {
    id: 'stage-02',
    tier: '入門',
    title: '一直線の競走',
    description: '壁なしの純粋なかけっこ。距離は同じでも先手はあなた。最短路を一歩も無駄にせず走り抜けよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 1, row: 4 },
          { col: 7, row: 4 },
        ],
        walls: [],
        wallsRemaining: [0, 0],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'easy' },
  },
  {
    id: 'stage-03',
    tier: '入門',
    title: '跳躍の心得',
    description: '細い回廊で相手と鉢合わせ。目の前のポーンは飛び越えられる。一足飛びで抜き去ろう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 3 },
          { col: 6, row: 4 },
        ],
        walls: [
          { col: 5, row: 3, orientation: 'v', owner: 1 },
          { col: 6, row: 3, orientation: 'v', owner: 1 },
        ],
        wallsRemaining: [5, 3],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'easy' },
  },
  {
    id: 'stage-04',
    tier: '入門',
    title: '関門封鎖',
    description: '足の速いCPUが一歩先行。仕込んだ関門の出口を壁でふさぎ、遠回りさせて逆転しよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 4 },
          { col: 2, row: 3 },
        ],
        walls: [
          { col: 1, row: 1, orientation: 'v', owner: 0 },
          { col: 3, row: 1, orientation: 'v', owner: 0 },
        ],
        wallsRemaining: [8, 4],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'easy' },
  },
  {
    id: 'stage-05',
    tier: '入門',
    title: '五分の競り合い',
    description: '壁も距離も互角の中盤戦。先手の利を守りつつ、走るか止めるかを見極めよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 3, row: 4 },
          { col: 5, row: 4 },
        ],
        walls: [
          { col: 2, row: 2, orientation: 'h', owner: 0 },
          { col: 0, row: 5, orientation: 'v', owner: 0 },
          { col: 4, row: 5, orientation: 'h', owner: 1 },
          { col: 6, row: 3, orientation: 'v', owner: 1 },
        ],
        wallsRemaining: [6, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'easy' },
  },
  // --- 初級 ---
  {
    id: 'stage-06',
    tier: '初級',
    title: '峠の攻防',
    description: '壁がひしめく峠道だが、あなたの前だけは開いている。相手が壁を回り込む前に駆け下りよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 4 },
          { col: 2, row: 6 },
        ],
        walls: [
          { col: 5, row: 4, orientation: 'v', owner: 1 },
          { col: 1, row: 5, orientation: 'h', owner: 0 },
          { col: 3, row: 6, orientation: 'v', owner: 1 },
          { col: 4, row: 1, orientation: 'v', owner: 0 },
        ],
        wallsRemaining: [7, 4],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'casual' },
  },
  {
    id: 'stage-07',
    tier: '初級',
    title: '壁の家計簿',
    description: '相手は一歩先だが壁は残り2枚。豊富な手持ち壁で足止めし、逆転の走りにつなげよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 3, row: 4 },
          { col: 5, row: 3 },
        ],
        walls: [
          { col: 2, row: 5, orientation: 'h', owner: 1 },
          { col: 4, row: 6, orientation: 'h', owner: 1 },
          { col: 4, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [8, 2],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'casual' },
  },
  {
    id: 'stage-08',
    tier: '初級',
    title: '忍びの抜け穴',
    description: '右翼は長い壁で封鎖済み。忍者の壁越え(wallJump)なら遠回りせず一気に突破できる。',
    config: {
      characters: ['ninja', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 7, row: 3 },
          { col: 1, row: 4 },
        ],
        walls: [
          { col: 5, row: 4, orientation: 'h', owner: 1 },
          { col: 7, row: 4, orientation: 'h', owner: 1 },
          { col: 1, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'casual' },
  },
  {
    id: 'stage-09',
    tier: '初級',
    title: '韋駄天の一番槍',
    description: '左端の一本道はあなた専用の走路。ダッシュの2マス突進でテンポを稼いで駆け抜けよう。',
    config: {
      characters: ['sprinter', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 0, row: 4 },
          { col: 5, row: 4 },
        ],
        walls: [
          { col: 2, row: 5, orientation: 'v', owner: 1 },
          { col: 4, row: 3, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'casual' },
  },
  {
    id: 'stage-10',
    tier: '初級',
    title: '氷河の滑走',
    description: '眼前に延びる氷の川。踏み込めば出口まで一気に滑走できる。加速を活かして先に着け。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [
        { type: 'ice', col: 6, row: 3 },
        { type: 'ice', col: 6, row: 4 },
        { type: 'ice', col: 6, row: 5 },
      ],
      position: {
        pawns: [
          { col: 6, row: 1 },
          { col: 2, row: 3 },
        ],
        walls: [
          { col: 1, row: 2, orientation: 'h', owner: 0 },
          { col: 3, row: 5, orientation: 'v', owner: 1 },
        ],
        wallsRemaining: [7, 7],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'casual' },
  },
  // --- 中級 ---
  {
    id: 'stage-11',
    tier: '中級',
    title: '泥濘の関門',
    description: '盤央のぬかるみは踏むと次の移動が封じられる。迂回か強行かを見極めよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [
        { type: 'swamp', col: 3, row: 4 },
        { type: 'swamp', col: 4, row: 4 },
        { type: 'swamp', col: 5, row: 4 },
      ],
      position: {
        pawns: [
          { col: 4, row: 2 },
          { col: 4, row: 6 },
        ],
        walls: [
          { col: 2, row: 2, orientation: 'h', owner: 1 },
          { col: 5, row: 5, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 7],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'normal' },
  },
  {
    id: 'stage-12',
    tier: '中級',
    title: '攻防の要',
    description: '互角の中盤。良い壁を数手さして相手を止めよう。',
    config: {
      characters: ['none', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 4, row: 4 },
          { col: 4, row: 3 },
        ],
        walls: [
          { col: 3, row: 3, orientation: 'v', owner: 0 },
          { col: 2, row: 5, orientation: 'h', owner: 1 },
          { col: 5, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'normal' },
  },
  {
    id: 'stage-13',
    tier: '中級',
    title: '韋駄天の間合い',
    description: '忍者を相手に横並びの競走。ダッシュの一手をどこで切るかが勝負。',
    config: {
      characters: ['sprinter', 'ninja'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 2, row: 4 },
          { col: 6, row: 4 },
        ],
        walls: [
          { col: 1, row: 5, orientation: 'h', owner: 1 },
          { col: 5, row: 2, orientation: 'h', owner: 0 },
          { col: 4, row: 6, orientation: 'v', owner: 1 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'normal' },
  },
  {
    id: 'stage-14',
    tier: '中級',
    title: '氷路の駆け引き',
    description: '中央の氷道は双方が使える高速路。滑るか、壁で止めるか、魔術師との読み合い。',
    config: {
      characters: ['ninja', 'mage'],
      gimmicks: [
        { type: 'ice', col: 4, row: 3 },
        { type: 'ice', col: 4, row: 4 },
        { type: 'ice', col: 4, row: 5 },
      ],
      position: {
        pawns: [
          { col: 4, row: 1 },
          { col: 4, row: 7 },
        ],
        walls: [
          { col: 1, row: 2, orientation: 'h', owner: 0 },
          { col: 6, row: 5, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [6, 7],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'normal' },
  },
  {
    id: 'stage-15',
    tier: '中級',
    title: '氷泥の十字',
    description: '氷で滑り込むか、泥を避けて着実に進むか。呪術師に手番を奪われる前提で計画せよ。',
    config: {
      characters: ['sprinter', 'hexer'],
      gimmicks: [
        { type: 'ice', col: 3, row: 6 },
        { type: 'ice', col: 3, row: 7 },
        { type: 'swamp', col: 4, row: 2 },
        { type: 'swamp', col: 5, row: 2 },
      ],
      position: {
        pawns: [
          { col: 3, row: 4 },
          { col: 5, row: 4 },
        ],
        walls: [
          { col: 4, row: 4, orientation: 'v', owner: 1 },
          { col: 2, row: 3, orientation: 'h', owner: 1 },
          { col: 5, row: 6, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'normal' },
  },
  // --- 上級 ---
  {
    id: 'stage-16',
    tier: '上級',
    title: '泥沼の呪縛',
    description: '行く手の要所に沼、相手は手番を奪う呪術師。1手飛ばされても崩れない進路を選べ。',
    config: {
      characters: ['none', 'hexer'],
      gimmicks: [
        { type: 'swamp', col: 5, row: 6 },
        { type: 'swamp', col: 3, row: 6 },
        { type: 'swamp', col: 4, row: 2 },
      ],
      position: {
        pawns: [
          { col: 5, row: 4 },
          { col: 3, row: 5 },
        ],
        walls: [
          { col: 4, row: 5, orientation: 'h', owner: 1 },
          { col: 6, row: 3, orientation: 'v', owner: 0 },
          { col: 2, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-17',
    tier: '上級',
    title: '氷獄の関門',
    description: '氷と沼が入り交じる終盤、重戦士のCPUは一歩先。忍者の跳躍を切り札に関門を突破せよ。',
    config: {
      characters: ['ninja', 'heavyweight'],
      gimmicks: [
        { type: 'ice', col: 3, row: 6 },
        { type: 'ice', col: 4, row: 6 },
        { type: 'ice', col: 5, row: 6 },
        { type: 'ice', col: 4, row: 1 },
        { type: 'ice', col: 4, row: 2 },
        { type: 'swamp', col: 1, row: 6 },
        { type: 'swamp', col: 7, row: 6 },
      ],
      position: {
        pawns: [
          { col: 1, row: 4 },
          { col: 4, row: 3 },
        ],
        walls: [
          { col: 2, row: 5, orientation: 'v', owner: 1 },
          { col: 5, row: 3, orientation: 'h', owner: 0 },
          { col: 0, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 9],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-18',
    tier: '上級',
    title: '大盾の砦',
    description: '重戦士のCPUが壁の砦で待ち構える。魔術師の一撃でどこを崩すかが勝負の分かれ目。',
    config: {
      characters: ['mage', 'heavyweight'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 3, row: 5 },
          { col: 6, row: 4 },
        ],
        walls: [
          { col: 0, row: 6, orientation: 'h', owner: 1 },
          { col: 2, row: 6, orientation: 'h', owner: 1 },
          { col: 5, row: 6, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [8, 7],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-19',
    tier: '上級',
    title: '氷路の競走',
    description: '双方の眼前に氷の滑走路。先に相手の滑走路を壁で断て。ただし魔術師のCPUは壁を1枚消してくる。',
    config: {
      characters: ['none', 'mage'],
      gimmicks: [
        { type: 'ice', col: 2, row: 5 },
        { type: 'ice', col: 2, row: 6 },
        { type: 'ice', col: 2, row: 7 },
        { type: 'ice', col: 6, row: 1 },
        { type: 'ice', col: 6, row: 2 },
        { type: 'ice', col: 6, row: 3 },
      ],
      position: {
        pawns: [
          { col: 2, row: 2 },
          { col: 6, row: 5 },
        ],
        walls: [
          { col: 4, row: 4, orientation: 'v', owner: 1 },
          { col: 0, row: 3, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [7, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-20',
    tier: '上級',
    title: '影走り',
    description: 'CPUは壁を飛び越える忍者。囲い込みは一度だけ破られる前提で、二重の備えを組み立てろ。',
    config: {
      characters: ['sprinter', 'ninja'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 4 },
          { col: 2, row: 3 },
        ],
        walls: [
          { col: 1, row: 1, orientation: 'h', owner: 0 },
          { col: 3, row: 2, orientation: 'v', owner: 1 },
          { col: 0, row: 2, orientation: 'v', owner: 1 },
          { col: 5, row: 5, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [7, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  // --- 達人 ---
  {
    id: 'stage-21',
    tier: '達人',
    title: '楔抜きの計',
    description: '重戦士の砦が行く手を塞ぎ、壁数もあちらが上。魔術師の消去で楔の一枚を抜き、細道を駆け抜けよ。',
    config: {
      characters: ['mage', 'heavyweight'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 7, row: 4 },
          { col: 1, row: 4 },
        ],
        walls: [
          { col: 6, row: 4, orientation: 'h', owner: 1 },
          { col: 5, row: 4, orientation: 'v', owner: 1 },
          { col: 5, row: 6, orientation: 'v', owner: 1 },
          { col: 1, row: 1, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [3, 7],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-22',
    tier: '達人',
    title: '白刃の跳躍',
    description: '二重の柵の向こうで強敵が先を急ぐ。忍者の壁越えで柵を破り、僅差の競走を制せ。',
    config: {
      characters: ['ninja', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 2, row: 5 },
          { col: 6, row: 4 },
        ],
        walls: [
          { col: 1, row: 5, orientation: 'h', owner: 1 },
          { col: 3, row: 5, orientation: 'h', owner: 1 },
          { col: 5, row: 1, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [4, 3],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'tough' },
  },
  {
    id: 'stage-23',
    tier: '達人',
    title: '呪いの一手',
    description: '読みの深さで勝る強敵と真っ向勝負。呪いで手番を奪う一瞬にだけ、勝ち筋が開く。',
    config: {
      characters: ['hexer', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 4 },
          { col: 4, row: 4 },
        ],
        walls: [
          { col: 3, row: 1, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [4, 4],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-24',
    tier: '達人',
    title: '泥海の開拓者',
    description: '氷も沼も相手に味方し、重戦士の壁が要塞を築く。探検家の地形無効と斜め歩きで、隙間を踏み抜け。',
    config: {
      characters: ['explorer', 'heavyweight'],
      gimmicks: [
        { type: 'ice', col: 6, row: 4 },
        { type: 'ice', col: 6, row: 3 },
        { type: 'swamp', col: 2, row: 5 },
        { type: 'swamp', col: 4, row: 5 },
        { type: 'swamp', col: 7, row: 5 },
      ],
      position: {
        pawns: [
          { col: 5, row: 5 },
          { col: 6, row: 5 },
        ],
        walls: [
          { col: 3, row: 6, orientation: 'h', owner: 1 },
          { col: 5, row: 6, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [3, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-25',
    tier: '達人',
    title: '氷上の駆け引き',
    description: '氷の走路の先に、最強の読みと壁数が立ちはだかる。走路を割られても、韋駄天の突進で先に滑り込め。',
    config: {
      characters: ['sprinter', 'none'],
      gimmicks: [
        { type: 'ice', col: 4, row: 4 },
        { type: 'ice', col: 4, row: 5 },
        { type: 'ice', col: 4, row: 6 },
      ],
      position: {
        pawns: [
          { col: 4, row: 3 },
          { col: 2, row: 4 },
        ],
        walls: [],
        wallsRemaining: [3, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  // --- 極 ---
  {
    id: 'stage-26',
    tier: '極',
    title: '鉄壁の関門',
    description: '呪術師の砦が待ち構える。手番を一度奪われる前提で計画し、忍びの跳躍で関門を抜けよ。',
    config: {
      characters: ['ninja', 'hexer'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 3 },
          { col: 1, row: 3 },
        ],
        walls: [
          { col: 1, row: 5, orientation: 'h', owner: 1 },
          { col: 3, row: 5, orientation: 'h', owner: 1 },
          { col: 5, row: 5, orientation: 'h', owner: 1 },
          { col: 7, row: 4, orientation: 'h', owner: 1 },
          { col: 0, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [4, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-27',
    tier: '極',
    title: '泥濘の死線',
    description: '沼の帯が盤を分かつ。探検家の免疫で泥を突っ切れ。ただし魔術師は頼みの壁を消しにくる。',
    config: {
      characters: ['explorer', 'mage'],
      gimmicks: [
        { type: 'swamp', col: 1, row: 2 },
        { type: 'swamp', col: 2, row: 2 },
        { type: 'swamp', col: 3, row: 2 },
        { type: 'swamp', col: 4, row: 2 },
        { type: 'swamp', col: 5, row: 2 },
        { type: 'swamp', col: 6, row: 2 },
        { type: 'swamp', col: 7, row: 2 },
      ],
      position: {
        pawns: [
          { col: 1, row: 4 },
          { col: 4, row: 5 },
        ],
        walls: [
          { col: 0, row: 5, orientation: 'h', owner: 1 },
          { col: 3, row: 6, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [4, 4],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-28',
    tier: '極',
    title: '氷走の逆襲',
    description: '壁を跳ぶ忍者が一歩先を行く。氷の川を一息に滑り抜け、先着で覆せ。',
    config: {
      characters: ['none', 'ninja'],
      gimmicks: [
        { type: 'ice', col: 1, row: 4 },
        { type: 'ice', col: 1, row: 5 },
      ],
      position: {
        pawns: [
          { col: 1, row: 3 },
          { col: 5, row: 4 },
        ],
        walls: [
          { col: 2, row: 3, orientation: 'h', owner: 1 },
        ],
        wallsRemaining: [3, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-29',
    tier: '極',
    title: '一穴突破',
    description: '行く手を長城が塞ぐ。魔術師の消去はどの一枚に使うべきか、崩し所を見極めよ。',
    config: {
      characters: ['mage', 'none'],
      gimmicks: [],
      position: {
        pawns: [
          { col: 6, row: 4 },
          { col: 2, row: 4 },
        ],
        walls: [
          { col: 0, row: 5, orientation: 'h', owner: 1 },
          { col: 2, row: 5, orientation: 'h', owner: 1 },
          { col: 4, row: 5, orientation: 'h', owner: 1 },
          { col: 6, row: 5, orientation: 'h', owner: 1 },
          { col: 7, row: 6, orientation: 'h', owner: 1 },
          { col: 1, row: 2, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [4, 5],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
  {
    id: 'stage-30',
    tier: '極',
    title: '終焉の城壁',
    description: '最後の敵は壁を蓄えた重戦士。氷道と沼を読み切り、韋駄天の一閃で最後の城壁を破れ。',
    config: {
      characters: ['sprinter', 'heavyweight'],
      gimmicks: [
        { type: 'ice', col: 2, row: 5 },
        { type: 'ice', col: 2, row: 6 },
        { type: 'swamp', col: 1, row: 4 },
        { type: 'swamp', col: 3, row: 4 },
        { type: 'swamp', col: 6, row: 1 },
      ],
      position: {
        pawns: [
          { col: 2, row: 3 },
          { col: 6, row: 5 },
        ],
        walls: [
          { col: 3, row: 6, orientation: 'h', owner: 1 },
          { col: 5, row: 6, orientation: 'h', owner: 1 },
          { col: 7, row: 2, orientation: 'v', owner: 1 },
          { col: 6, row: 1, orientation: 'h', owner: 0 },
        ],
        wallsRemaining: [4, 6],
        currentPlayer: 0,
      },
    },
    cpu: { level: 'hard' },
  },
];

/** id からステージを引く。未知の id は undefined。 */
export function stageById(id) {
  return STAGES.find((s) => s.id === id);
}

/** 次のステージ（クリア後の「次のステージ」導線用）。末尾なら null。 */
export function nextStage(id) {
  const i = STAGES.findIndex((s) => s.id === id);
  if (i < 0 || i + 1 >= STAGES.length) return null;
  return STAGES[i + 1];
}
